import { useEffect, useState } from "react";

// How often (ms) to re-read the native bridge, which has no change events.
const BRIDGE_POLL = 60000;

// Reports the device's battery to the kiosk banner from whichever source this
// runtime offers, or null if none. Two sources, in priority order:
//
// 1. "web" — the W3C Battery Status API (navigator.getBattery). Present in real
//    Chrome, and it pushes change events. NOT implemented in Android WebView,
//    which is what our kiosk wrapper (com.datagentes.activityboard) renders in —
//    so on the tablets this returns nothing and source 2 has to carry it.
// 2. "bridge" — an object the wrapper injects via addJavascriptInterface as
//    `window.AndroidBattery`, with getLevel() -> 0..100 and isCharging() -> bool
//    (both synchronous primitives; that's all a JS interface can return). No
//    events, so we poll.
//
// Returns { level: 0..1, charging: bool, source } — level normalized to 0..1 for
// both sources so callers don't care which one answered.
export default function useBatteryStatus() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (typeof navigator === "undefined" && typeof window === "undefined") return;

    let cancelled = false;

    // --- 2. Native bridge injected by the kiosk wrapper --------------------
    const bridge = typeof window !== "undefined" ? window.AndroidBattery : null;
    if (bridge && typeof bridge.getLevel === "function") {
      const readBridge = () => {
        try {
          const pct = Number(bridge.getLevel());
          if (cancelled || !Number.isFinite(pct)) return;
          const charging =
            typeof bridge.isCharging === "function" ? !!bridge.isCharging() : false;
          setStatus({ level: Math.min(1, Math.max(0, pct / 100)), charging, source: "bridge" });
        } catch (err) {
          /* bridge threw; leave the last good reading in place */
        }
      };
      readBridge();
      const id = setInterval(readBridge, BRIDGE_POLL);
      return () => { cancelled = true; clearInterval(id); };
    }

    // --- 1. Battery Status API --------------------------------------------
    if (!navigator.getBattery) return;

    let battery = null;
    const read = () => {
      if (!cancelled && battery)
        setStatus({ level: battery.level, charging: battery.charging, source: "web" });
    };

    navigator
      .getBattery()
      .then((b) => {
        if (cancelled) return;
        battery = b;
        read();
        b.addEventListener("levelchange", read);
        b.addEventListener("chargingchange", read);
      })
      .catch(() => {
        /* permission-policy blocked or unavailable; stay hidden */
      });

    return () => {
      cancelled = true;
      if (battery) {
        battery.removeEventListener("levelchange", read);
        battery.removeEventListener("chargingchange", read);
      }
    };
  }, []);

  return status;
}
